import React, { useState, useEffect, useRef } from 'react';
import { Loader2, Check } from 'lucide-react';
import { authApi, type SessionTokens } from '../api/services';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { QrCode } from '@/components/QrCode';
import { PageHeader } from '@/components/ui/page-header';
import { PageContainer } from '@/components/ui/page-container';

// ---------------------------------------------------------------------------
// MfaSetupPage — three-step authenticator setup flow:
//   1. Scan QR code (or copy the shared key manually)
//   2. Verify the first 6-digit code
//   3. Save the recovery codes
//
// Lives inside the authenticated app shell, so it doesn't use the dark glass
// AuthBackdrop the unauthenticated auth pages share.
// ---------------------------------------------------------------------------

const STEP_LABELS = ['Scan QR Code', 'Verify Code', 'Save Recovery Codes'];

export default function MfaSetupPage() {
  const { markMfaEnabled, applyTokens } = useAuth();
  const navigate = useNavigate();
  // The fresh session enabling returns (its token no longer says two-step is pending).
  const [freshSession, setFreshSession] = useState<SessionTokens | null>(null);
  const [step, setStep] = useState(0); // 0=loading, 1=show QR, 2=verify, 3=done
  const [qrUri, setQrUri] = useState('');
  const [manualKey, setManualKey] = useState('');
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // Setup long after signing in asks for the current password (or a fresh sign-in).
  const [needPassword, setNeedPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');

  // Once: each setup makes a new key, so a second call (React's development
  // double mount) would replace the key behind the QR code on screen.
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    setupMfa();
  }, []);

  const setupMfa = async (password?: string) => {
    setLoading(true);
    try {
      const data = await authApi.setupMfa(password);
      setQrUri(data.authenticatorUri || (data as any).qrUri || '');
      setManualKey(data.sharedKey || (data as any).key || (data as any).authenticatorKey || '');
      setNeedPassword(false);
      setCurrentPassword('');
      setError('');
      setStep(1);
    } catch (err: any) {
      const data = err?.response?.data;
      if (data?.code === 'current_password_required') {
        setNeedPassword(true);
        setError('');
      } else {
        if (data?.code === 'wrong_password') setCurrentPassword('');
        setError(data?.error || 'Failed to initialise MFA setup. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const verifyAndEnable = async () => {
    if (code.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const result = await authApi.enableMfa(code);
      // The old tokens stopped working when two-step went on.
      if (result.accessToken) applyTokens(result);
      markMfaEnabled();
      if (result.accessToken) setFreshSession(result);
      setRecoveryCodes(result.recoveryCodes || []);
      setStep(3);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const activeStep = step === 0 ? 0 : step - 1;

  return (
    <PageContainer width="narrow">
      <PageHeader
        eyebrow="Security"
        title="Set Up Multi-Factor Authentication"
        subtitle="Protect your account with an authenticator app like Microsoft Authenticator, Google Authenticator, or Authy."
      />

      <Card>
        <CardContent className="py-6">
          {/* Stepper */}
          <ol className="mb-6 flex items-center justify-between">
            {STEP_LABELS.map((label, i) => {
              const complete = i < activeStep;
              const current = i === activeStep;
              return (
                <React.Fragment key={label}>
                  <li className="flex flex-col items-center gap-2">
                    <div
                      className={cn(
                        'grid h-7 w-7 place-items-center rounded-full border-2 text-xs font-semibold',
                        complete && 'border-emerald-500 bg-emerald-500 text-white',
                        current && 'border-primary bg-primary text-primary-foreground',
                        !complete && !current && 'border-border text-muted-foreground',
                      )}
                    >
                      {complete ? <Check className="h-3.5 w-3.5" /> : i + 1}
                    </div>
                    <p
                      className={cn(
                        'text-xs font-medium',
                        current ? 'text-foreground' : 'text-muted-foreground',
                      )}
                    >
                      {label}
                    </p>
                  </li>
                  {i < STEP_LABELS.length - 1 && (
                    <div
                      aria-hidden
                      className={cn(
                        'mx-2 h-px flex-1',
                        i < activeStep ? 'bg-emerald-500' : 'bg-border',
                      )}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </ol>

          {error && (
            <Alert variant="error" className="mb-3">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Step 0: the current password, when the sign-in isn't recent */}
          {step === 0 && needPassword && (
            <form
              onSubmit={(e) => { e.preventDefault(); if (currentPassword) setupMfa(currentPassword); }}
              className="flex flex-col gap-3"
            >
              <p className="text-sm">For your security, enter your current password to set up two-step sign-in.</p>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="mfa-current-password">Current password</Label>
                <Input
                  id="mfa-current-password"
                  type="password"
                  autoComplete="current-password"
                  autoFocus
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                />
              </div>
              <Button type="submit" size="lg" className="w-full gap-2" disabled={loading || !currentPassword}>
                {loading && <Loader2 className="h-5 w-5 animate-spin" />}
                Continue
              </Button>
            </form>
          )}

          {/* Step 0: Loading */}
          {step === 0 && !needPassword && !error && (
            <div className="py-10 text-center">
              <Loader2 className="mx-auto h-7 w-7 animate-spin text-muted-foreground" />
              <p className="mt-3 text-sm text-muted-foreground">Preparing MFA setup...</p>
            </div>
          )}

          {/* Step 1: Show QR Code */}
          {step === 1 && (
            <>
              <p className="text-sm">Scan this QR code with your authenticator app:</p>
              {qrUri && (
                <div className="my-6 flex justify-center">
                  <QrCode value={qrUri} size={220} label="Scan this QR code with your authenticator app" />
                </div>
              )}
              <p className="text-sm text-muted-foreground">Can't scan? Enter this key manually:</p>
              <div className="mb-4 mt-2 rounded-lg border border-border bg-muted/40 p-3 text-center font-mono text-sm tracking-widest text-primary">
                {manualKey}
              </div>
              <Button onClick={() => setStep(2)} size="lg" className="w-full">
                I've scanned the code — Next
              </Button>
            </>
          )}

          {/* Step 2: Verify */}
          {step === 2 && (
            <>
              <p className="mb-4 text-sm">
                Enter the 6-digit code from your authenticator app to verify setup:
              </p>
              <div className="mb-4 flex flex-col gap-1.5">
                <Label htmlFor="mfa-code">6-digit code</Label>
                <Input
                  id="mfa-code"
                  inputMode="numeric"
                  maxLength={6}
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  onKeyDown={(e) => { if (e.key === 'Enter') verifyAndEnable(); }}
                  className="h-12 text-center font-mono text-2xl font-semibold tracking-[0.3em]"
                />
              </div>
              <Button
                onClick={verifyAndEnable}
                disabled={loading || code.length !== 6}
                size="lg"
                className="w-full gap-2"
              >
                {loading && <Loader2 className="h-5 w-5 animate-spin" />}
                {loading ? 'Verifying…' : 'Verify & Enable MFA'}
              </Button>
              <Button
                variant="ghost"
                onClick={() => setStep(1)}
                className="mt-1 w-full"
              >
                Back to QR code
              </Button>
            </>
          )}

          {/* Step 3: Recovery Codes */}
          {step === 3 && (
            <>
              <Alert variant="success" className="mb-4">
                <AlertDescription>MFA is now enabled on your account.</AlertDescription>
              </Alert>
              <p className="mb-1 text-base font-semibold">Save your recovery codes</p>
              <p className="mb-3 text-sm text-muted-foreground">
                If you lose access to your authenticator app, you can use these one-time codes to
                sign in. Store them somewhere safe — you won't be able to see them again.
              </p>
              <div className="mb-4 rounded-lg border border-border bg-muted/40 p-3">
                {recoveryCodes.map((rc, i) => (
                  <p key={i} className="py-0.5 font-mono text-sm text-emerald-600 dark:text-emerald-400">
                    {rc}
                  </p>
                ))}
              </div>
              <Button
                onClick={() => navigator.clipboard.writeText(recoveryCodes.join('\n'))}
                className="mb-2 w-full"
              >
                Copy Recovery Codes
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  // Carry on with the fresh session: signing out here also lost
                  // a new account's setup (it has no way back in yet but its
                  // welcome link). Older servers sent no session: sign in again.
                  if (freshSession) {
                    applyTokens(freshSession);
                    sessionStorage.removeItem('mfaRequired');
                    sessionStorage.removeItem('mfaSuggested');
                    // In-app, so the session just applied is the one that carries on.
                    navigate('/', { replace: true });
                  } else {
                    sessionStorage.clear();
                    window.location.href = '/';
                  }
                }}
                className="w-full"
              >
                {freshSession ? 'Done' : 'Done — Sign in again'}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </PageContainer>
  );
}
